import React from "react";
import { Menu, Dropdown, Icon, Label, Segment } from "semantic-ui-react";
import moment from "moment";
import { useAppContext } from "../hooks/useAppContext";
import { PermitMetricScopeInput } from "../operations/queries/getPermitsCountByStatesMetric";
import MetricPeriodSelector, { MetricPeriod } from "./MetricPeriodSelector";

interface Props {
  value: PermitMetricScopeInput;
  onChange: (value: PermitMetricScopeInput) => void;
}

export const DynamicScopeSelector = ({ value, onChange }: Props) => {
  const { myProjects, myRegisters } = useAppContext();

  // ── Projects ──────────────────────────────────────────────────────────────
  const projectOptions = myProjects ? myProjects.map((p) => ({ key: p.id, value: p.id, text: p.name })) : [];

  const getFilteredRegisters = () => {
    if (!myRegisters) return [];
    if (!value.projectIds || value.projectIds.length === 0) return myRegisters;
    return myRegisters.filter((r) => r.project && value.projectIds?.includes(r.project.id));
  };

  const registerOptions = getFilteredRegisters().map((r) => ({
    key: r.id,
    value: r.id,
    text: `${r.project?.name} – ${r.title}`,
  }));

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleProjectChange = (_: any, { value: selected }: any) => {
    const nextProjectIds = selected as string[];
    const nextRegisterIds = (value.registerIds ?? []).filter((regId) => {
      const reg = myRegisters?.find((r) => r.id === regId);
      return reg?.project && nextProjectIds.includes(reg.project.id);
    });
    onChange({
      ...value,
      projectIds: nextProjectIds.length > 0 ? nextProjectIds : null,
      registerIds: nextRegisterIds.length > 0 ? nextRegisterIds : null,
    });
  };

  const handleRegisterChange = (_: any, { value: selected }: any) => {
    const nextRegisterIds = selected as string[];
    onChange({
      ...value,
      registerIds: nextRegisterIds.length > 0 ? nextRegisterIds : null,
    });
  };

  const handlePeriodChange = (period: MetricPeriod) => {
    onChange({ ...value, dateFrom: period.dateFrom, dateTo: period.dateTo });
  };

  // ── Display helpers ────────────────────────────────────────────────────────
  const selectedProjectCount = value.projectIds?.length ?? 0;
  const selectedRegisterCount = value.registerIds?.length ?? 0;

  const periodLabel = (() => {
    if (!value.dateFrom && !value.dateTo) return "All time";
    if (value.dateFrom && value.dateTo) {
      const diffDays = moment(value.dateTo).diff(moment(value.dateFrom), "days") + 1;
      if (diffDays === 7) return "Last 7 days";
      if (diffDays === 30) return "Last 30 days";
      if (diffDays === 90) return "Last 90 days";
      const isYtd =
        moment(value.dateFrom).isSame(moment().startOf("year"), "day") && moment(value.dateTo).isSame(moment(), "day");
      if (isYtd) return "This year";
      return `${moment(value.dateFrom).format("MMM D")} – ${moment(value.dateTo).format("MMM D, YYYY")}`;
    }
    return "Custom";
  })();

  // ── Name lookups (for chips) ───────────────────────────────────────────────
  const projectName = (id: string) => myProjects?.find((p) => p.id === id)?.name ?? id;
  const registerName = (id: string) => {
    const reg = myRegisters?.find((r) => r.id === id);
    return reg ? `${reg.project?.name} – ${reg.title}` : id;
  };

  const removeProject = (id: string) => {
    const nextProjectIds = (value.projectIds ?? []).filter((p) => p !== id);
    const nextRegisterIds = (value.registerIds ?? []).filter((regId) => {
      const reg = myRegisters?.find((r) => r.id === regId);
      return reg?.project && nextProjectIds.includes(reg.project.id);
    });
    onChange({
      ...value,
      projectIds: nextProjectIds.length > 0 ? nextProjectIds : null,
      registerIds: nextRegisterIds.length > 0 ? nextRegisterIds : null,
    });
  };

  const removeRegister = (id: string) => {
    const nextRegisterIds = (value.registerIds ?? []).filter((r) => r !== id);
    onChange({ ...value, registerIds: nextRegisterIds.length > 0 ? nextRegisterIds : null });
  };

  const hasActiveFilters = selectedProjectCount > 0 || selectedRegisterCount > 0;

  return (
    <>
      <Menu pointing secondary color="green">
        <Menu.Item header>
          <Icon name="filter" />
          Filters
        </Menu.Item>

        <Menu.Menu position="right">
          {/* ── Projects ── */}
          <Menu.Item style={{ paddingBottom: "0em" }}>
            <Dropdown
              placeholder="Projects"
              basic
              clearable
              multiple
              //search
              options={projectOptions}
              value={value.projectIds ?? []}
              onChange={handleProjectChange}
              trigger={
                <div className="divider text">
                  {selectedProjectCount > 0 ? `Projects (${selectedProjectCount})` : "Projects"}
                </div>
              }
              renderLabel={() => null}
            />
          </Menu.Item>

          {/* ── Registers ── */}
          <Menu.Item style={{ paddingBottom: "0em" }}>
            <Dropdown
              placeholder="Registers"
              basic
              clearable
              multiple
              // search
              disabled={projectOptions.length === 0}
              options={registerOptions}
              value={value.registerIds ?? []}
              onChange={handleRegisterChange}
              trigger={
                <div className="divider text">
                  {selectedRegisterCount > 0 ? `Registers (${selectedRegisterCount})` : "Registers"}
                </div>
              }
              renderLabel={() => null}
            />
          </Menu.Item>

          {/* ── Period ── */}
          <Menu.Item>
            <Dropdown
              basic
              trigger={
                <>
                  <Icon name="calendar alternate outline" />
                  {periodLabel}
                </>
              }
            >
              <Dropdown.Menu style={{ marginTop: "0.8em" }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Segment basic secondary>
                  <MetricPeriodSelector
                    value={{ dateFrom: value.dateFrom ?? null, dateTo: value.dateTo ?? null }}
                    onChange={handlePeriodChange}
                  />
                </Segment>
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      {/* ── Active filter chips ─────────────────────────────────────────────── */}
      {hasActiveFilters && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4em", marginTop: "-0.5em", marginBottom: "0.75em" }}>
          {(value.projectIds ?? []).map((id) => (
            <Label key={`p-${id}`} color="blue" size="small">
              <Icon name="folder" />
              {projectName(id)}
              <Icon name="delete" link onClick={() => removeProject(id)} />
            </Label>
          ))}
          {(value.registerIds ?? []).map((id) => (
            <Label key={`r-${id}`} color="purple" size="small">
              <Icon name="clipboard list" />
              {registerName(id)}
              <Icon name="delete" link onClick={() => removeRegister(id)} />
            </Label>
          ))}
        </div>
      )}
    </>
  );
};
